import React, { useRef, useEffect, useState } from 'react';
import classnames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import classNames from 'classnames';

const useStyles = createUseStyles((theme: Theme) => ({
  section: {
    maxHeight: 2500,
    transition: 'max-height .3s',
    overflow: 'hidden',
  },
  sectionOverBackground: {
    position: 'relative',
  },
  title: {
    ...theme.fonts.h3,
    color: theme.colors.inkPrimary,
    marginBottom: theme.marginBase * 3,
  },
  titleFinal: {
    marginTop: theme.marginBase * 7,
    color: theme.colors.white,
  },
  description: {
    ...theme.fonts.body,
    color: theme.colors.inkSecondary,
    marginBottom: theme.marginBase * 3,
  },
  sectionHidden: {
    maxHeight: 0,
  },
  sectionAfterAnimation: {
    overflow: 'visible',
  },
  bottomSpace: {
    height: theme.marginBase * 9,
  },
  finalBackground: {
    position: 'absolute',
    left: 0,
    right: 0,
    height: 320,
    backgroundColor: theme.colors.accent,
  },
}));

export interface SetupSectionProps {
  title: string;
  description?: string;
  children: React.ReactNode;
  noScroll?: boolean;
  visible?: boolean;
  trackId?: string;
  finalSection?: boolean;
}

export const SetupSection = ({
  title,
  description,
  children,
  visible = true,
  noScroll,
  trackId,
  finalSection,
}: SetupSectionProps) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });

  const [animationEnded, setAnimationEnded] = useState(false);
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const prevVisibleRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (visible && prevVisibleRef.current === false) {
      const ref = sectionRef.current;
      if (ref && !noScroll) {
        setTimeout(() => {
          ref.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }, 100);
      }

      setTimeout(() => setAnimationEnded(true), 300);
    }

    if (visible && prevVisibleRef.current === null) {
      setAnimationEnded(true);
    }

    if (!visible && prevVisibleRef.current) {
      setAnimationEnded(false);
    }

    prevVisibleRef.current = visible;
  }, [visible, prevVisibleRef, noScroll, setAnimationEnded]);

  return (
    <>
      <div
        className={classNames({
          [classes.finalBackground]: finalSection && visible,
        })}
      />
      <section
        className={classnames(classes.section, {
          [classes.sectionHidden]: !visible,
          [classes.sectionAfterAnimation]: animationEnded,
          [classes.sectionOverBackground]: finalSection,
        })}
        ref={sectionRef}
      >
        {!!visible && (
          <>
            <h2
              className={classNames(classes.title, {
                [classes.titleFinal]: finalSection,
              })}
              id={trackId}
            >
              {title}
            </h2>
            {description && (
              <h2 className={classes.description}>{description}</h2>
            )}
            {children}
            <div className={classes.bottomSpace} />
          </>
        )}
      </section>
    </>
  );
};
